import { useState } from 'react';
import axios from 'utils/axiosInstance';
import { Pcomment } from 'types/pcomment';

interface CommentFormProps {
    noteId: number;
    onCommentCreated: (comment: Pcomment) => void;
    isLoggedIn: boolean;
    parentId?: number;
    onCancel?: () => void;
}

const CommentForm = ({ noteId, onCommentCreated, isLoggedIn, parentId = 0, onCancel }: CommentFormProps) => {
    const [content, setContent] = useState('');
    const [isHidden, setIsHidden] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isLoggedIn) {
            alert('로그인이 필요한 서비스입니다.');
            return;
        }

        if (!content.trim()) {
            alert('댓글 내용을 입력해주세요.');
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.post(`/api/v1/notes/${noteId}/pcomments`, {
                pcomment: {
                    content: content.trim(),
                    parent_id: parentId,
                    reply: parentId > 0,
                    hidden: isHidden // Include hidden status
                }
            });

            if (response.data.success) {
                setContent('');
                setIsHidden(false); // Reset hidden status
                onCommentCreated(response.data.item);
                if (onCancel) onCancel();
            } else {
                alert(response.data.message || '댓글 작성 중 오류가 발생했습니다.');
            }
        } catch (error: any) {
            console.error('댓글 작성 실패:', error);
            alert(error.response?.data?.message || '댓글 작성 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isLoggedIn) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <p className="text-gray-600 mb-4">댓글 작성은 회원만 가능합니다.</p>
                <button
                    onClick={() => window.location.href = '/users/sign_in'}
                    className="btn btn-primary btn-sm"
                >
                    로그인
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
                <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder={parentId > 0 ? "답글을 입력하세요..." : "댓글을 입력하세요..."}
                    className="textarea textarea-bordered w-full h-24 focus:textarea-primary"
                    maxLength={250}
                    disabled={isLoading}
                ></textarea>
                <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                    {content.length}/250
                </div>
            </div>
            <div className="flex justify-between items-center gap-2">
                <label className="label cursor-pointer justify-start gap-2 py-0">
                    <input
                        type="checkbox"
                        className="checkbox checkbox-primary checkbox-sm"
                        checked={isHidden}
                        onChange={(e) => setIsHidden(e.target.checked)}
                    />
                    <span className="label-text text-sm">비공개</span>
                </label>

                <div className="flex gap-2">
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isLoading}
                            className="btn btn-ghost btn-sm"
                        >
                            취소
                        </button>
                    )}
                    <button
                        type="submit"
                        disabled={isLoading || !content.trim()}
                        className="btn btn-primary btn-sm min-w-[80px]"
                    >
                        {isLoading ? <span className="loading loading-spinner loading-xs"></span> : (parentId > 0 ? '답글 등록' : '댓글 등록')}
                    </button>
                </div>
            </div>
        </form>
    );
};

export default CommentForm;
